import { ApolloClient } from '@apollo/client';

const AUTH_CHANNEL_NAME = 'auth_broadcast_channel';
const AUTH_SOURCE_ID = Math.random().toString(36).slice(2);

export type AuthEventType = 'LOGIN' | 'LOGOUT';

interface AuthEventMessage {
  type: AuthEventType;
  sourceId: string;
  createdAt: number;
}

export const clearSessionScopedApolloCache = (
  client: ApolloClient<unknown>
) => {
  client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'profile' });
  client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'item' });
  client.cache.gc();
};

export const broadcastAuthEvent = (type: AuthEventType) => {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return;

  const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
  channel.postMessage({
    type,
    sourceId: AUTH_SOURCE_ID,
    createdAt: Date.now()
  });
  channel.close();
};

export const publishLogoutAuthEvent = (client: ApolloClient<unknown>) => {
  clearSessionScopedApolloCache(client);
  broadcastAuthEvent('LOGOUT');
};

export const subscribeToAuthEvents = (
  onEvent: (type: AuthEventType) => void
) => {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window))
    return () => {};

  const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
  const subscribedAt = Date.now();
  let pendingAuthChange = false;

  const handleAuthChange = (type: AuthEventType) => {
    if (document.visibilityState === 'visible') onEvent(type);
    else {
      pendingAuthChange = true;
    }
  };

  channel.onmessage = (event: MessageEvent<AuthEventMessage>) => {
    if (
      !event.data?.type ||
      event.data.sourceId === AUTH_SOURCE_ID ||
      event.data.createdAt < subscribedAt
    )
      return;

    handleAuthChange(event.data.type);
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible' && pendingAuthChange) {
      pendingAuthChange = false;
      window.location.reload();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    channel.close();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};
